import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CommonScrollBar from "../../Common/CommonScrollBar";
import CommonDateTime from "../../Common/CommonDateTime";
import { WARRANTY_PLANS_URL, WARRANTY_PLAN_DRAFT_URL } from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import ReasonDialog from "../common/ReasonDialog";
import {
  createPlanDraft,
  discardPlanDraft,
  getPlanDraft,
  listProductGroups,
  listSampleCycles,
  previewPlanDraft,
  updatePlanDraft,
} from "../common/warrantyApi";
import { isAmbiguousFailure, newClientRequestId, requestIdFor, settleRequestId } from "../common/clientRequest";
import { confirmLeave, setUnsavedChanges } from "../common/unsavedGuard";
import { useWarrantyNav } from "../common/useWarrantyNav";
import {
  contentFromForm,
  formFromContent,
  knownPaths,
  plainIssues,
  validateDraftForm,
  websiteModelsFrom,
} from "./editor/draftModel";
import BasicsSection from "./editor/BasicsSection";
import AppContentSection from "./editor/AppContentSection";
import BenefitsSection from "./editor/BenefitsSection";
import TenuresSettingsSection from "./editor/TenuresSettingsSection";
import PricingSection from "./editor/PricingSection";
import AddonsCouponSection from "./editor/AddonsCouponSection";
import OverridesSection from "./editor/OverridesSection";
import SegmentsSection from "./editor/SegmentsSection";
import PreviewPanel, { MAX_SAMPLES } from "./editor/PreviewPanel";
import PublishDialog from "./PublishDialog";
import "../warranty.css";

const AUTOSAVE_MS = 2000;
const PREVIEW_IDLE_MS = 400;
const SEARCH_IDLE_MS = 400;

const SECTIONS = [
  ["basics", "Basics"],
  ["app", "App content"],
  ["benefits", "Benefits"],
  ["tenures", "Tenures and settings"],
  ["pricing", "Pricing"],
  ["addons", "Add-ons and coupon"],
  ["overrides", "Overrides"],
  ["segments", "Bike segments"],
];

const SAVE_LABEL = {
  saved: "Saved",
  saving: "Saving",
  unsaved: "Unsaved changes",
  invalid: "Not saved: fix the highlighted fields",
  conflict: "Not saved: changed elsewhere",
  error: "Not saved: could not reach the server",
};

const PlanDraftEditorScreen = () => {
  const { roomId: draftId } = useParams();
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const navigate = useNavigate();
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.PLANS_ADMIN);
  useWarrantyNav(allowed);

  const [load, setLoad] = useState({ status: "loading", message: "" });
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(null);
  const [savedJson, setSavedJson] = useState("");
  const [saveState, setSaveState] = useState("saved");
  const [saveMessage, setSaveMessage] = useState("");
  const [conflict, setConflict] = useState(false);
  const [finalMessage, setFinalMessage] = useState("");
  const [serverIssues, setServerIssues] = useState([]);
  const [preview, setPreview] = useState({ status: "idle", data: null, message: "" });
  // The OMS product group names an override can use: "loading", "ready" or "error". The same answer
  // carries the website models a bike segment lists (14 Oct 2026); absent reads as none.
  const [productGroups, setProductGroups] = useState({ status: "loading", names: [], models: [] });
  const [defaults, setDefaults] = useState([]);
  const [samples, setSamples] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchState, setSearchState] = useState({ status: "idle", message: "", count: 0 });
  const [showDiscard, setShowDiscard] = useState(false);
  const [showRestart, setShowRestart] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  // What the service holds after a refused save: null, or { status: "loading" | "error" | "ready", view }.
  const [conflictCopy, setConflictCopy] = useState(null);
  const [previewTick, setPreviewTick] = useState(0);

  const mounted = useRef(true);
  const revisionRef = useRef(0);
  const savingRef = useRef(false);
  // A save whose outcome is unknown (network error, 503): { id, revision, json, content }. It is
  // sent again, unchanged, before anything newer, so the service's idempotency key can replay it.
  const pendingSaveRef = useRef(null);
  const previewSeq = useRef(0);
  const searchSeq = useRef(0);
  const discardIdRef = useRef(null);
  const restartDiscardIdRef = useRef(null);
  const restartCreateIdRef = useRef(null);
  const restartDiscarded = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      setUnsavedChanges(false);
    };
  }, []);

  // ---------------------------------------------------------------- load
  const loadDraft = useCallback(async () => {
    setLoad({ status: "loading", message: "" });
    const result = await getPlanDraft(accessToken, draftId);
    if (!mounted.current) {
      return;
    }
    if (!result.ok) {
      setLoad({ status: "error", message: result.message, notFound: result.notFound });
      return;
    }
    const view = result.data;
    const nextForm = formFromContent(view.content);
    revisionRef.current = view.revision;
    pendingSaveRef.current = null;
    // The baseline is the model's own reading of the stored content, so key order or a blank
    // optional never shows up as an unsaved change. If the stored draft still holds values the
    // form no longer offers (see contentFromForm), the baseline is the content with them in, so
    // the next autosave writes the cleaned content back.
    setSavedJson(JSON.stringify(contentFromForm(nextForm, { keepHidden: true }).content));
    setForm(nextForm);
    setMeta({
      id: view.id,
      tier: view.tier,
      status: view.status,
      stale: view.stale,
      scheduled: view.scheduled || [],
      basedOnPlanVersionId: view.basedOnPlanVersionId,
      updatedBy: view.updatedBy,
      updatedAt: view.updatedAt,
    });
    setServerIssues(plainIssues(view.issues));
    setConflict(false);
    setConflictCopy(null);
    setSaveMessage("");
    setSaveState("saved");
    setFinalMessage(
      view.status === "open" ? "" : `This draft was ${view.status}, so it cannot be changed. Go back to the plans.`
    );
    setLoad({ status: "ready", message: "" });
  }, [accessToken, draftId]);

  useEffect(() => {
    if (allowed) {
      loadDraft();
    }
  }, [allowed, loadDraft]);

  // ---------------------------------------------------------------- derived
  const built = useMemo(() => (form ? contentFromForm(form) : null), [form]);
  const contentJson = built && !built.invalid ? JSON.stringify(built.content) : null;
  const latestJsonRef = useRef(null);
  latestJsonRef.current = contentJson;
  const formInvalid = built !== null && built.invalid;
  // The group check runs only when the list loaded and has names; otherwise the field is free text.
  const groupNames = productGroups.status === "ready" && productGroups.names.length > 0 ? productGroups.names : null;
  const websiteModels = productGroups.models;
  const clientErrors = useMemo(
    () => (form ? validateDraftForm(form, { productGroupNames: groupNames, models: websiteModels }) : {}),
    [form, groupNames, websiteModels]
  );
  const known = useMemo(() => (form ? knownPaths(form) : new Set()), [form]);
  const locked = conflict || finalMessage !== "";

  const messagesByPath = useMemo(() => {
    const byPath = {};
    const add = (path, severity, message) => {
      if (!byPath[path]) {
        byPath[path] = [];
      }
      if (!byPath[path].some((item) => item.message === message)) {
        byPath[path].push({ severity, message });
      }
    };
    const serverErrorPaths = new Set(
      serverIssues.filter((issue) => issue.severity !== "warning").map((issue) => issue.path)
    );
    // The service's own answer wins when both name the same field, unless the form holds a number
    // the service has not seen (then its issues are about older content).
    Object.keys(clientErrors).forEach((path) => {
      if (formInvalid || !serverErrorPaths.has(path)) {
        add(path, "error", clientErrors[path]);
      }
    });
    serverIssues.forEach((issue) => {
      if (formInvalid && issue.severity !== "warning" && clientErrors[issue.path] !== undefined) {
        return;
      }
      add(issue.path, issue.severity === "warning" ? "warning" : "error", issue.message);
    });
    return byPath;
  }, [clientErrors, serverIssues, formInvalid]);
  const msgs = useCallback((path) => messagesByPath[path] || [], [messagesByPath]);
  // Whatever names a path with no field on screen is listed at the top, so nothing that blocks
  // Publish is invisible: the service's issues, and the form's own errors on a path that has no field.
  const topIssues = [
    ...Object.keys(clientErrors)
      .filter(
        (path) =>
          !known.has(path) && !serverIssues.some((issue) => issue.path === path && issue.severity !== "warning")
      )
      .map((path) => ({ path, severity: "error", message: clientErrors[path] })),
    ...serverIssues.filter((issue) => !known.has(issue.path)),
  ];
  // Fields (or whole-draft paths) with at least one error, counted once however many sources say so.
  const errorCount = Object.keys(messagesByPath).filter((path) =>
    messagesByPath[path].some((item) => item.severity === "error")
  ).length;

  const dirty = saveState !== "saved";

  // ---------------------------------------------------------------- unsaved guard
  useEffect(() => {
    setUnsavedChanges(dirty && load.status === "ready" && finalMessage === "");
  }, [dirty, load.status, finalMessage]);

  useEffect(() => {
    if (!dirty) {
      return undefined;
    }
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // ---------------------------------------------------------------- autosave
  // After a refused save, read what the service holds now, without touching the form, so the
  // person can keep their typing on top of it or take the stored version.
  const readConflictCopy = useCallback(async () => {
    setConflictCopy({ status: "loading", view: null });
    const result = await getPlanDraft(accessToken, draftId);
    if (!mounted.current) {
      return;
    }
    setConflictCopy(result.ok ? { status: "ready", view: result.data } : { status: "error", view: null });
  }, [accessToken, draftId]);

  const save = useCallback(
    async (json, content) => {
      if (savingRef.current) {
        return;
      }
      savingRef.current = true;
      setSaveState("saving");
      setSaveMessage("");
      // An earlier save with an unknown outcome goes first, unchanged: same id, same revision, same
      // body. If the service had applied it, the OMS replays the stored answer and the revision it
      // returns is the one the newer content must be saved against. Only then does newer typing go.
      const attempt = pendingSaveRef.current || {
        id: newClientRequestId(),
        revision: revisionRef.current,
        json,
        content,
      };
      const result = await updatePlanDraft(accessToken, draftId, attempt.revision, attempt.content, attempt.id);
      savingRef.current = false;
      pendingSaveRef.current = isAmbiguousFailure(result) ? attempt : null;
      if (!mounted.current) {
        return;
      }
      if (result.ok) {
        revisionRef.current = result.data.revision;
        // The issues are about the content just saved; if the form moved on, the next preview
        // brings the right ones.
        if (attempt.json === latestJsonRef.current) {
          setServerIssues(plainIssues(result.data.issues));
        }
        setMeta((prev) =>
          prev
            ? {
                ...prev,
                stale: result.data.stale,
                scheduled: result.data.scheduled || [],
                updatedBy: result.data.updatedBy,
                updatedAt: result.data.updatedAt,
              }
            : prev
        );
        setSavedJson(attempt.json);
      } else if (result.code === "PLAN_DRAFT_REVISION_CONFLICT") {
        setConflict(true);
        setSaveState("conflict");
        setSaveMessage(result.message);
        readConflictCopy();
      } else if (result.code === "PLAN_DRAFT_NOT_OPEN" || result.code === "PLAN_DRAFT_NOT_FOUND") {
        setFinalMessage(result.message);
        setSaveState("error");
      } else {
        setSaveState("error");
        setSaveMessage(result.message);
      }
    },
    [accessToken, draftId, readConflictCopy]
  );

  // "Keep editing": the form stays as the person typed it and is saved on top of the version the
  // service holds now (their typing wins over the other change, which they were told).
  const keepEditing = () => {
    if (conflictCopy === null || conflictCopy.status !== "ready") {
      return;
    }
    const view = conflictCopy.view;
    revisionRef.current = view.revision;
    pendingSaveRef.current = null;
    setMeta((prev) =>
      prev
        ? {
            ...prev,
            status: view.status,
            stale: view.stale,
            scheduled: view.scheduled || [],
            updatedBy: view.updatedBy,
            updatedAt: view.updatedAt,
          }
        : prev
    );
    setConflict(false);
    setConflictCopy(null);
    setSaveMessage("");
    if (view.status !== "open") {
      setFinalMessage(`This draft was ${view.status}, so it cannot be changed. Go back to the plans.`);
      setSaveState("error");
      return;
    }
    // The next autosave sends the form at the new revision.
    setSavedJson("");
    setSaveState("unsaved");
    setPreviewTick((tick) => tick + 1);
  };

  useEffect(() => {
    if (load.status !== "ready" || locked) {
      return undefined;
    }
    if (formInvalid) {
      setSaveState("invalid");
      return undefined;
    }
    // A save with an unknown outcome still has to be sent again, even if the form is back to what
    // was last known to be saved: the service may hold it already.
    if (contentJson === savedJson && pendingSaveRef.current === null) {
      setSaveState((prev) => (savingRef.current ? prev : "saved"));
      return undefined;
    }
    setSaveState((prev) => (savingRef.current ? prev : "unsaved"));
    const timer = setTimeout(() => save(contentJson, built.content), AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [load.status, locked, formInvalid, contentJson, savedJson, save, built]);

  const retrySave = () => {
    if (contentJson !== null) {
      save(contentJson, built.content);
    }
  };

  // ---------------------------------------------------------------- product groups
  // Once per editor. A failed or malformed answer leaves the field as free text.
  useEffect(() => {
    if (!allowed) {
      return undefined;
    }
    let cancelled = false;
    // A refetch (a new token) keeps the list it has until the answer arrives.
    setProductGroups((prev) => (prev.status === "ready" ? prev : { status: "loading", names: [], models: [] }));
    (async () => {
      const result = await listProductGroups(accessToken);
      if (cancelled || !mounted.current) {
        return;
      }
      if (!result.ok || !Array.isArray(result.data?.items)) {
        setProductGroups({ status: "error", names: [], models: [] });
        return;
      }
      const names = result.data.items
        .map((item) => item?.name)
        .filter((name) => typeof name === "string" && name !== "");
      setProductGroups({ status: "ready", names, models: websiteModelsFrom(result.data) });
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, allowed]);

  // ---------------------------------------------------------------- samples
  useEffect(() => {
    if (!allowed) {
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await listSampleCycles(accessToken);
      if (cancelled || !mounted.current) {
        return;
      }
      const items = result.ok ? result.data.items || [] : [];
      setDefaults(items);
      setSamples(items.slice(0, MAX_SAMPLES));
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, allowed]);

  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      searchSeq.current += 1;
      setSearchResults([]);
      setSearchState({ status: "idle", message: "", count: 0 });
      return undefined;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      setSearchState({ status: "loading", message: "", count: 0 });
      const result = await listSampleCycles(accessToken, term);
      if (seq !== searchSeq.current || !mounted.current) {
        return;
      }
      if (result.ok) {
        setSearchResults(result.data.items || []);
        setSearchState({ status: "ready", message: "", count: (result.data.items || []).length });
      } else {
        setSearchState({ status: "error", message: result.message, count: 0 });
      }
    }, SEARCH_IDLE_MS);
    return () => clearTimeout(timer);
  }, [search, accessToken]);

  const candidates = useMemo(() => {
    const seen = new Set();
    const list = [];
    [...defaults, ...searchResults, ...(samples || [])].forEach((item) => {
      if (!seen.has(item.key)) {
        seen.add(item.key);
        list.push(item);
      }
    });
    return list;
  }, [defaults, searchResults, samples]);

  const toggleSample = (candidate) =>
    setSamples((prev) => {
      const chosen = prev || [];
      if (chosen.some((item) => item.key === candidate.key)) {
        return chosen.length <= 1 ? chosen : chosen.filter((item) => item.key !== candidate.key);
      }
      return chosen.length >= MAX_SAMPLES ? chosen : [...chosen, candidate];
    });

  // ---------------------------------------------------------------- preview
  useEffect(() => {
    if (load.status !== "ready" || finalMessage !== "" || samples === null) {
      return undefined;
    }
    // Whatever is still in flight was for older content: drop its answer now.
    const seq = ++previewSeq.current;
    if (contentJson === null) {
      return undefined;
    }
    setPreview((prev) => ({ ...prev, status: "loading" }));
    const timer = setTimeout(async () => {
      const body = { content: built.content };
      if (samples.length > 0) {
        body.samples = samples;
      }
      const result = await previewPlanDraft(accessToken, draftId, body);
      if (seq !== previewSeq.current || !mounted.current) {
        return;
      }
      if (result.ok) {
        setPreview({ status: "ready", data: result.data, message: "" });
        setServerIssues(plainIssues(result.data.issues));
      } else {
        setPreview((prev) => ({ ...prev, status: "error", message: result.message }));
      }
    }, PREVIEW_IDLE_MS);
    return () => clearTimeout(timer);
  }, [load.status, finalMessage, contentJson, built, samples, accessToken, draftId, previewTick]);

  // ---------------------------------------------------------------- actions
  const goBack = () => {
    if (confirmLeave()) {
      navigate(WARRANTY_PLANS_URL);
    }
  };

  const confirmDiscard = async (reason) => {
    const result = await discardPlanDraft(
      accessToken,
      draftId,
      reason,
      requestIdFor(discardIdRef, `discard:${draftId}:${reason}`)
    );
    settleRequestId(discardIdRef, result);
    if (!mounted.current) {
      return { ok: true };
    }
    if (result.ok) {
      setUnsavedChanges(false);
      toast.success("The draft was discarded.");
      navigate(WARRANTY_PLANS_URL);
      return { ok: true };
    }
    return { ok: false, message: result.message };
  };

  // Stale draft: discard it, then start a new one from the live version. Each step has its own
  // request id, and a retry after the discard worked does not discard again.
  const confirmRestart = async (reason) => {
    if (!restartDiscarded.current) {
      const discarded = await discardPlanDraft(
        accessToken,
        draftId,
        reason,
        requestIdFor(restartDiscardIdRef, `restart-discard:${draftId}:${reason}`)
      );
      settleRequestId(restartDiscardIdRef, discarded);
      if (!mounted.current) {
        return { ok: true };
      }
      if (!discarded.ok && discarded.code !== "PLAN_DRAFT_NOT_OPEN") {
        return { ok: false, message: discarded.message };
      }
      restartDiscarded.current = true;
    }
    const body = { tier: meta.tier, basedOnPlanVersionId: null };
    const created = await createPlanDraft(
      accessToken,
      body,
      requestIdFor(restartCreateIdRef, `restart-create:${meta.tier}`)
    );
    settleRequestId(restartCreateIdRef, created);
    if (!mounted.current) {
      return { ok: true };
    }
    if (created.ok) {
      setUnsavedChanges(false);
      toast.success("A new draft was started from the live version.");
      navigate(`${WARRANTY_PLAN_DRAFT_URL}/${encodeURIComponent(created.data.id)}`);
      return { ok: true };
    }
    if (created.code === "PLAN_DRAFT_EXISTS" && created.details?.draftId) {
      setUnsavedChanges(false);
      navigate(`${WARRANTY_PLAN_DRAFT_URL}/${encodeURIComponent(created.details.draftId)}`);
      return { ok: true };
    }
    return {
      ok: false,
      message: `The old draft was discarded, but the new one could not be started. ${created.message}`,
    };
  };

  if (!allowed) {
    return (
      <section className="Main_section">
        <div className="warranty-page">
          <EmptyState title="You do not have access to Assure plans">
            <span>Ask an admin to give you the Warranty Assure Plans permission.</span>
          </EmptyState>
        </div>
      </section>
    );
  }

  const title = form && form.displayName.trim() !== "" ? form.displayName : meta ? meta.tier : "Plan";
  const publishBlockers = [];
  if (saveState !== "saved") {
    publishBlockers.push("Wait for the draft to be saved.");
  }
  if (errorCount > 0) {
    publishBlockers.push(`Fix ${errorCount} error${errorCount === 1 ? "" : "s"} first.`);
  }
  if (locked) {
    publishBlockers.push("This draft can no longer be changed.");
  }
  if (meta && meta.stale) {
    publishBlockers.push("The live version changed: start again from it.");
  }
  const canPublish = publishBlockers.length === 0 && load.status === "ready";

  return (
    <section className="Main_section">
      <CommonScrollBar className={"ScrollbarsSidebar"} height={{ height: "calc(100vh - 90px)" }}>
        <div className="warranty-page">
          <div className="warranty-page-head">
            <div>
              <h4>{title} draft</h4>
              <p className="warranty-sub">
                {meta ? `Tier ${meta.tier}, market IN. ` : ""}
                {meta && meta.updatedAt
                  ? `Last saved by ${meta.updatedBy}, ${CommonDateTime(meta.updatedAt, "LL, h:mm A", "show")}.`
                  : ""}
              </p>
            </div>
            <div className="warranty-page-actions">
              <span className={`warranty-save-state is-${saveState}`} role="status" aria-live="polite">
                {SAVE_LABEL[saveState]}
              </span>
              {saveState === "error" && !locked && (
                <button type="button" className="warranty-inline-btn" onClick={retrySave}>
                  Retry save
                </button>
              )}
              <button type="button" className="warranty-inline-btn" onClick={goBack}>
                Back to plans
              </button>
              <button
                type="button"
                className="warranty-inline-btn is-danger"
                onClick={() => setShowDiscard(true)}
                disabled={load.status !== "ready" || finalMessage !== ""}
              >
                Discard draft
              </button>
              <Button
                className="Addbtn"
                disabled={!canPublish}
                onClick={() => setShowPublish(true)}
                title={canPublish ? "Review and publish this draft" : publishBlockers.join(" ")}
                aria-describedby="warranty-publish-hint"
              >
                Publish
              </Button>
            </div>
          </div>
          <div id="warranty-publish-hint" className="warranty-publish-hint">
            {load.status === "ready" && !canPublish ? publishBlockers.join(" ") : ""}
          </div>

          {load.status === "loading" && <Loading label="Loading the draft" />}
          {load.status === "error" && (
            <ErrorState message={load.message} onRetry={load.notFound ? undefined : loadDraft} />
          )}

          {load.status === "ready" && form && (
            <>
              {conflict && (
                <div className="warranty-banner is-error" role="alert">
                  <span>
                    This draft was changed elsewhere. What you typed is still shown here and is not saved.
                    {conflictCopy !== null && conflictCopy.status === "loading" && " Reading the latest version."}
                    {conflictCopy !== null &&
                      conflictCopy.status === "error" &&
                      " The latest version could not be read, so Keep editing is not available yet."}
                    {conflictCopy !== null &&
                      conflictCopy.status === "ready" &&
                      " Keep editing saves your version over the other change."}
                  </span>
                  <span className="warranty-banner-actions">
                    {conflictCopy !== null && conflictCopy.status === "error" && (
                      <button type="button" className="warranty-inline-btn" onClick={readConflictCopy}>
                        Retry
                      </button>
                    )}
                    <button
                      type="button"
                      className="warranty-inline-btn is-primary"
                      onClick={keepEditing}
                      disabled={conflictCopy === null || conflictCopy.status !== "ready"}
                    >
                      Keep editing
                    </button>
                    <button type="button" className="warranty-inline-btn is-danger" onClick={loadDraft}>
                      Reload and discard my changes
                    </button>
                  </span>
                </div>
              )}
              {finalMessage !== "" && !conflict && (
                <div className="warranty-banner is-error" role="alert">
                  <span>{finalMessage}</span>
                  <Link className="warranty-inline-btn" to={WARRANTY_PLANS_URL}>
                    Back to plans
                  </Link>
                </div>
              )}
              {saveState === "error" && saveMessage !== "" && (
                <div className="warranty-banner is-error" role="alert">
                  <span>{saveMessage}</span>
                </div>
              )}
              {meta && meta.stale && !locked && (
                <div className="warranty-banner is-warning" role="status">
                  <span>
                    The live version of this tier changed after the draft was started, so publishing it would be
                    refused.
                  </span>
                  <button type="button" className="warranty-inline-btn" onClick={() => setShowRestart(true)}>
                    Start again from the live version
                  </button>
                </div>
              )}
              {meta &&
                meta.scheduled.map((version) => (
                  <div className="warranty-banner is-info" role="status" key={version.id}>
                    <span>
                      A scheduled version starts on {CommonDateTime(version.effectiveFrom, "LL", "show")}: cancel it
                      before publishing.
                    </span>
                    <button type="button" className="warranty-inline-btn" onClick={goBack}>
                      Go to the plans
                    </button>
                  </div>
                ))}
              {topIssues.length > 0 && (
                <div className="warranty-banner is-list" role="status">
                  <strong>Also from the service:</strong>
                  <ul>
                    {topIssues.map((issue, index) => (
                      <li key={`${issue.path}-${index}`} className={`is-${issue.severity}`}>
                        {issue.severity === "warning" ? "Warning: " : "Error: "}
                        {issue.message}
                        {issue.path !== "" ? ` (${issue.path})` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <nav className="warranty-section-nav" aria-label="Sections">
                {SECTIONS.map(([id, label]) => (
                  <a
                    key={id}
                    href={`#warranty-sec-${id}`}
                    onClick={(event) => {
                      event.preventDefault();
                      const target = document.getElementById(`warranty-sec-${id}`);
                      if (target) {
                        target.scrollIntoView({ block: "start" });
                        target.focus({ preventScroll: true });
                      }
                    }}
                  >
                    {label}
                  </a>
                ))}
              </nav>

              <div className="warranty-editor">
                <div className="warranty-editor-main formloginset">
                  <BasicsSection form={form} setForm={setForm} msgs={msgs} locked={locked} tier={meta.tier} />
                  <AppContentSection form={form} setForm={setForm} msgs={msgs} locked={locked} />
                  <BenefitsSection form={form} setForm={setForm} msgs={msgs} locked={locked} />
                  <TenuresSettingsSection form={form} setForm={setForm} msgs={msgs} locked={locked} />
                  <PricingSection form={form} setForm={setForm} msgs={msgs} locked={locked} />
                  <AddonsCouponSection form={form} setForm={setForm} msgs={msgs} locked={locked} />
                  <OverridesSection
                    form={form}
                    setForm={setForm}
                    msgs={msgs}
                    locked={locked}
                    productGroups={productGroups}
                  />
                  <SegmentsSection
                    form={form}
                    setForm={setForm}
                    msgs={msgs}
                    locked={locked}
                    models={websiteModels}
                    modelsLoading={productGroups.status === "loading"}
                  />
                </div>
                <PreviewPanel
                  preview={preview}
                  formInvalid={formInvalid}
                  content={built ? built.content : null}
                  samples={samples || []}
                  candidates={candidates}
                  onToggleSample={toggleSample}
                  search={search}
                  onSearchChange={setSearch}
                  searchState={searchState}
                  onRetry={() => setPreviewTick((tick) => tick + 1)}
                />
              </div>
            </>
          )}
        </div>
      </CommonScrollBar>

      {meta && (
        <PublishDialog
          show={showPublish}
          draftId={draftId}
          revision={revisionRef.current}
          tierName={title}
          termsVersion={form ? form.termsVersion : ""}
          samples={samples || []}
          scheduled={meta.scheduled}
          onClose={({ reload }) => {
            setShowPublish(false);
            if (reload) {
              loadDraft();
            }
          }}
          onPublished={() => {
            setUnsavedChanges(false);
            setShowPublish(false);
            navigate(WARRANTY_PLANS_URL);
          }}
        />
      )}
      <ReasonDialog
        show={showDiscard}
        title="Discard this draft"
        explanation="The draft and everything typed into it is thrown away. The live version and any scheduled version are not touched."
        confirmLabel="Discard draft"
        onConfirm={confirmDiscard}
        onClose={() => setShowDiscard(false)}
      />
      <ReasonDialog
        show={showRestart}
        title="Start again from the live version"
        explanation="This discards the current draft, including any changes in it, and starts a new draft that copies the live version."
        confirmLabel="Discard and start again"
        onConfirm={confirmRestart}
        onClose={() => setShowRestart(false)}
      />
    </section>
  );
};

// The same route serves every draft, so the screen is keyed by the draft id: opening another draft
// (after "Start again from the live version") starts with fresh state and fresh request ids.
const PlanDraftEditor = () => {
  const { roomId } = useParams();
  return <PlanDraftEditorScreen key={roomId} />;
};

export default PlanDraftEditor;
